import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Document {
  id: string;
  name: string;
  uploadDate: string;
  size: string;
  summary?: string;
  status?: string;
  learning_preferences?: string;
  topic_id?: string | null;
  thumbnail_url?: string | null;
}

interface Session {
  id: string;
  title: string;
  last_activity_at: string;
  total_duration_seconds: number;
  topic_id?: string | null;
}

interface Topic {
  id: string;
  title: string;
  instructions?: string;
  created_at: string;
  icon?: string;
}

// Helper function to format date
const formatDate = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 24) {
    return 'Today';
  } else if (diffInHours < 48) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString();
  }
};

// Helper function to format file size
const formatFileSize = (bytes: number | null) => {
  if (!bytes) return 'Unknown size';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

// Query hook for fetching all user documents
export const useDocuments = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['documents', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('documents')
        .select('id, original_filename, upload_timestamp, file_size_bytes, summary, status')
        .eq('user_id', user.id)
        .order('upload_timestamp', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        throw error;
      }

      const formattedDocuments: Document[] = data.map(doc => ({
        id: doc.id,
        name: doc.original_filename,
        uploadDate: formatDate(doc.upload_timestamp),
        size: formatFileSize(doc.file_size_bytes),
        summary: doc.summary || undefined,
        status: doc.status || 'processed'
      }));

      return formattedDocuments;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
  });
};

// Query hook for fetching a specific document
export const useDocument = (documentId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['document', documentId],
    queryFn: async () => {
      if (!documentId || !user) return null;
      
      const { data, error } = await supabase
        .from('documents')
        .select('id, original_filename, upload_timestamp, file_size_bytes, summary, learning_preferences, topic_id')
        .eq('id', documentId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching document:', error);
        throw error;
      }

      if (!data) return null;

      const formattedDocument: Document = {
        id: data.id,
        name: data.original_filename,
        uploadDate: formatDate(data.upload_timestamp),
        size: formatFileSize(data.file_size_bytes),
        summary: data.summary || undefined,
        learning_preferences: data.learning_preferences || undefined,
        topic_id: data.topic_id || null
      };

      return formattedDocument;
    },
    enabled: !!documentId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Query hook for fetching a specific topic
export const useTopic = (topicId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['topic', topicId],
    queryFn: async () => {
      if (!topicId || !user) return null;
      
      const { data, error } = await supabase
        .from('topics')
        .select('id, title, instructions, created_at, icon')
        .eq('id', topicId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching topic:', error);
        throw error;
      }

      return data;
    },
    enabled: !!topicId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Query hook for fetching all user topics
export const useTopics = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['topics', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('topics')
        .select('id, title, instructions, created_at, icon')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching topics:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Query hook for fetching all user sessions
export const useSessions = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['sessions', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('sessions')
        .select('id, title, last_activity_at, total_duration_seconds, topic_id')
        .eq('user_id', user.id)
        .order('last_activity_at', { ascending: false });

      if (error) {
        console.error('Error fetching sessions:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Query hook for fetching sessions for a specific document
export const useDocumentSessions = (documentId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['document-sessions', documentId],
    queryFn: async () => {
      if (!documentId || !user) return [];
      
      const { data, error } = await supabase
        .from('sessions')
        .select('id, title, last_activity_at, total_duration_seconds, conversation_history')
        .eq('document_id', documentId)
        .eq('user_id', user.id)
        .order('last_activity_at', { ascending: false });

      if (error) {
        console.error('Error fetching document sessions:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!documentId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Query hook for fetching a specific session with document details
export const useSession = (sessionId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['session', sessionId],
    queryFn: async () => {
      if (!sessionId || !user) return null;
      
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          documents (
            id,
            original_filename
          )
        `)
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching session:', error);
        throw error;
      }

      return data;
    },
    enabled: !!sessionId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Query hook that fetches a session by ID or creates a new one from URL parameters
export const useSessionOrDocument = (id: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['session', id],
    queryFn: async () => {
      if (!id || !user) return null;
      
      // Handle "new" session creation from URL parameters
      if (id === 'new') {
        const urlParams = new URLSearchParams(window.location.search);
        const documentId = urlParams.get('document');
        const topicId = urlParams.get('topic');
        const description = urlParams.get('description') || '';
        
        if (!documentId) {
          throw new Error('Document ID is required for new session');
        }
        
        console.log('🆕 Creating new session with document:', documentId, 'topic:', topicId);
        
        // Get document details to create session title
        const { data: document, error: docError } = await supabase
          .from('documents')
          .select('original_filename, topic_id')
          .eq('id', documentId)
          .eq('user_id', user.id)
          .single();
          
        if (docError || !document) {
          throw new Error('Document not found or access denied');
        }
        
        // Use the document's topic_id if no topic specified in URL, otherwise use URL topic
        const sessionTopicId = topicId || document.topic_id;
        
        const nowIso = new Date().toISOString();
        const sessionTitle = `${document.original_filename.replace('.pdf', '')} - ${new Date().toLocaleDateString()}`;
        
        // Create new session in database with proper topic linkage
        const { data: newSession, error: sessionError } = await supabase
          .from('sessions')
          .insert({
            document_id: documentId,
            topic_id: sessionTopicId, // Link session to the same topic as document
            user_id: user.id,
            title: sessionTitle,
            conversation_history: [],
            last_pdf_page: 1,
            total_duration_seconds: 0,
            initial_prompt: description || null, // Store the initial prompt from textarea
            created_at: nowIso,
            updated_at: nowIso,
            last_activity_at: nowIso,
            current_session_start: nowIso
          })
          .select(`
            *,
            documents (
              id,
              original_filename
            )
          `)
          .single();
          
        if (sessionError) {
          console.error('❌ Error creating session:', sessionError);
          throw new Error(`Failed to create session: ${sessionError.message}`);
        }
        
        console.log('✅ Session created successfully with topic_id:', sessionTopicId);
        
        // Update the URL to the new session ID without reloading
        window.history.replaceState({}, '', `/session/${newSession.id}`);
        
        return newSession;
      }
      
      // Fetch existing session by ID with document details
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          *,
          documents (
            id,
            original_filename
          )
        `)
        .eq('id', id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (sessionError) {
        console.error('Error fetching session:', sessionError);
        throw sessionError;
      }

      if (!sessionData) {
        throw new Error('Session not found');
      }

      return sessionData;
    },
    enabled: !!id && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Query hook for fetching documents for a specific topic
export const useTopicDocuments = (topicId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['topic-documents', topicId],
    queryFn: async () => {
      if (!topicId || !user) return [];
      
      const { data, error } = await supabase
        .from('documents')
        .select('id, original_filename, upload_timestamp, file_size_bytes, summary, status, thumbnail_url')
        .eq('topic_id', topicId)
        .eq('user_id', user.id)
        .order('upload_timestamp', { ascending: false });
      
      if (error) {
        console.error('Error fetching topic documents:', error);
        throw error;
      }
      
      const formattedDocuments: Document[] = (data || []).map(doc => ({
        id: doc.id,
        name: doc.original_filename,
        uploadDate: formatDate(doc.upload_timestamp),
        size: formatFileSize(doc.file_size_bytes),
        summary: doc.summary || undefined,
        status: doc.status || 'processed',
        thumbnail_url: doc.thumbnail_url || null
      }));
      
      return formattedDocuments;
    },
    enabled: !!topicId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Query hook for fetching the single session for a specific topic (new architecture)
export const useTopicSession = (topicId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['topic-session', topicId],
    queryFn: async () => {
      if (!topicId || !user) return null;
      
      const { data, error } = await supabase
        .from('sessions')
        .select('id, title, last_activity_at, total_duration_seconds, session_summary, conversation_history, created_at, updated_at')
        .eq('topic_id', topicId)
        .eq('user_id', user.id)
        .maybeSingle(); // Expect only one session per topic
      
      if (error) {
        console.error('Error fetching topic session:', error);
        throw error;
      }
      
      // Safety net: if no session exists, create one
      if (!data) {
        console.log('No session found for topic, creating one...');
        
        // Get topic title first
        const { data: topicData } = await supabase
          .from('topics')
          .select('title')
          .eq('id', topicId)
          .eq('user_id', user.id)
          .single();
        
        // Create session
        const { data: newSessionData, error: createError } = await supabase
          .from('sessions')
          .insert({
            title: topicData?.title || 'Untitled Session',
            user_id: user.id,
            topic_id: topicId,
            title_source: 'auto_created'
          })
          .select('id, title, last_activity_at, total_duration_seconds, session_summary, conversation_history, created_at, updated_at')
          .single();
        
        if (createError) {
          console.error('Failed to create session:', createError);
          throw createError;
        }
        
        console.log('Session created successfully:', newSessionData);
        return newSessionData;
      }
      
      return data;
    },
    enabled: !!topicId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Legacy hook for backwards compatibility - will be removed after migration
export const useTopicSessions = (topicId: string | undefined) => {
  const session = useTopicSession(topicId);
  
  return {
    ...session,
    data: session.data ? [session.data] : [],
  };
};

// Study Notes interfaces and hooks
export interface StudyNote {
  id: string;
  user_id: string;
  topic_id: string;
  title: string;
  content: any; // BlockNote JSON
  created_at: string;
  updated_at: string;
  formatted_date?: string;
}

export const useTopicNotes = (topicId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['topic-notes', topicId],
    queryFn: async () => {
      if (!user?.id || !topicId) return [];
      
      const { data, error } = await supabase
        .from('study_notes')
        .select('*')
        .eq('user_id', user.id)
        .eq('topic_id', topicId)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching topic notes:', error);
        throw error;
      }

      return data.map(note => ({
        ...note,
        formatted_date: formatDate(note.updated_at)
      }));
    },
    enabled: !!user?.id && !!topicId,
  });
};

// Keep this for backward compatibility but mark as deprecated
export const useCreateStudyNote = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteData: { topic_id: string; title: string; content: any }) => {
      console.warn('⚠️ DEPRECATED: useCreateStudyNote should not be used anymore. Notes are created automatically with topics.');
      
      if (!user?.id) throw new Error('User not authenticated');

      console.log('🗂️ Creating study note (deprecated):', {
        topic_id: noteData.topic_id,
        title: noteData.title,
        contentLength: Array.isArray(noteData.content) ? noteData.content.length : 0
      });

      const { data, error } = await supabase
        .from('study_notes')
        .insert({
          user_id: user.id,
          topic_id: noteData.topic_id,
          title: noteData.title,
          content: noteData.content
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Failed to create study note:', error);
        throw error;
      }
      
      console.log('✅ Study note created successfully:', data.id);
      return data;
    },
    onSuccess: (data) => {
      // Invalidate topic notes query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['topic-notes', data.topic_id] });
    },
    onError: (error) => {
      console.error('💥 Study note creation failed:', error);
    },
    // Disable retries to prevent infinite loops
    retry: false,
  });
};

// Simplified - we can now remove the useCreateStudyNote export since it's handled in topic creation
export const useUpdateStudyNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteData: { id: string; title?: string; content?: any; topic_id: string }) => {
      console.log('🔄 Updating study note:', noteData.id);
      
      const updateData: any = {};
      if (noteData.title !== undefined) updateData.title = noteData.title;
      if (noteData.content !== undefined) updateData.content = noteData.content;

      const { data, error } = await supabase
        .from('study_notes')
        .update(updateData)
        .eq('id', noteData.id)
        .select()
        .single();

      if (error) {
        console.error('❌ Failed to update study note:', error);
        throw error;
      }
      
      console.log('✅ Study note updated successfully');
      return data;
    },
    onSuccess: (data) => {
      // Invalidate topic notes query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['topic-notes', data.topic_id] });
    },
    onError: (error) => {
      console.error('💥 Study note update failed:', error);
    },
    // Disable retries to prevent infinite loops
    retry: false,
  });
};

export const useDeleteStudyNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, topic_id }: { id: string; topic_id: string }) => {
      const { error } = await supabase
        .from('study_notes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, topic_id };
    },
    onSuccess: (data) => {
      // Invalidate topic notes query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['topic-notes', data.topic_id] });
    },
  });
};

// Document update mutation
export const useUpdateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: { original_filename?: string } }) => {
      const { data, error } = await supabase
        .from('documents')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Invalidate document queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['document', data.id] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['topic-documents'] });
    },
  });
};

// Topic Outline and Lessons Types
export interface TopicLesson {
  id: string;
  topic_id: string;
  user_id: string;
  type: 'lesson' | 'quiz';
  lesson_number: number;
  title: string;
  description: string | null;
  duration_minutes: number | null;
  learning_objectives: string[] | null;
  source_references: unknown[] | null;
  quiz_data: unknown | null;
  user_quiz_attempts: unknown | null;
  status: 'pending' | 'generating' | 'generated' | 'failed';
  completion_percentage: number;
  lesson_image_url: string | null;
  created_at: string;
  generated_at: string | null;
  updated_at: string;
}

export interface TopicOutlineData {
  course_title: string;
  course_description?: string;
  total_duration_minutes?: number;
  learner_level?: string;
  lesson_count?: number;
  generation_metadata?: Record<string, unknown>;
  internet_sources?: unknown[];
  ai_prompt?: string;
}

// Lesson Slide Type
export interface LessonSlide {
  id: string;
  lesson_id: string;
  user_id: string;
  topic_id: string;
  slide_id: string;
  slide_order: number;
  slide_type: string;
  section: string;
  title: string;
  content: Record<string, unknown>;
  structured: Record<string, unknown> | null;
  tutor_prompt: string | null;
  duration_minutes: number | null;
  completed: boolean;
  completed_at: string | null;
  time_spent_seconds: number;
  created_at: string;
  updated_at: string;
}

// Query hook for fetching topic outline and lessons
export const useTopicOutline = (topicId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['topic-outline', topicId],
    queryFn: async () => {
      if (!topicId || !user) return null;
      
      // Get topic with outline (using type assertion for new columns)
      const { data: topicData, error: topicError } = await supabase
        .from('topics')
        .select('course_outline, outline_accepted_at')
        .eq('id', topicId)
        .eq('user_id', user.id)
        .maybeSingle() as unknown as {
          data: {
            course_outline: TopicOutlineData | null;
            outline_accepted_at: string | null;
          } | null;
          error: Error | null;
        };

      if (topicError) {
        console.error('Error fetching topic outline:', topicError);
        throw topicError;
      }

      if (!topicData) {
        return null;
      }

      const hasOutline = !!topicData.course_outline && !!topicData.outline_accepted_at;
      
      if (!hasOutline) {
        return {
          has_outline: false,
          outline: null,
          lessons: [],
          quizzes: []
        };
      }

      // Get all lessons (using type assertion for new table)
      const { data: lessonsData, error: lessonsError } = await (supabase as any)
        .from('topic_lessons')
        .select('*')
        .eq('topic_id', topicId)
        .eq('user_id', user.id)
        .eq('type', 'lesson')
        .order('lesson_number', { ascending: true });

      if (lessonsError) {
        console.error('Error fetching lessons:', lessonsError);
        throw lessonsError;
      }

      // Get all quizzes (using type assertion for new table)
      const { data: quizzesData, error: quizzesError } = await (supabase as any)
        .from('topic_lessons')
        .select('*')
        .eq('topic_id', topicId)
        .eq('user_id', user.id)
        .eq('type', 'quiz')
        .order('lesson_number', { ascending: true });

      if (quizzesError) {
        console.error('Error fetching quizzes:', quizzesError);
        throw quizzesError;
      }

      return {
        has_outline: true,
        outline: topicData.course_outline,
        lessons: (lessonsData || []) as TopicLesson[],
        quizzes: (quizzesData || []) as TopicLesson[]
      };
    },
    enabled: !!topicId && !!user,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Query hook for fetching lesson with slides
export const useLessonWithSlides = (topicId: string | undefined, lessonId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['lesson-with-slides', topicId, lessonId],
    queryFn: async () => {
      if (!topicId || !lessonId || !user) return null;
      
      // Get lesson
      const { data: lessonData, error: lessonError } = await (supabase as any)
        .from('topic_lessons')
        .select('*')
        .eq('id', lessonId)
        .eq('topic_id', topicId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (lessonError) {
        console.error('Error fetching lesson:', lessonError);
        throw lessonError;
      }

      if (!lessonData) {
        return null;
      }

      // Get slides if lesson is generated
      if (lessonData.status === 'generated') {
        const { data: slidesData, error: slidesError } = await (supabase as any)
          .from('lesson_slides')
          .select('*')
          .eq('lesson_id', lessonId)
          .eq('user_id', user.id)
          .order('slide_order', { ascending: true });

        if (slidesError) {
          console.error('Error fetching slides:', slidesError);
          throw slidesError;
        }

        return {
          lesson: lessonData as TopicLesson,
          slides: (slidesData || []) as LessonSlide[]
        };
      }

      return {
        lesson: lessonData as TopicLesson,
        slides: []
      };
    },
    enabled: !!topicId && !!lessonId && !!user,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};